import { useEffect, useState } from "react";
import { getCurrentStaff, StaffUser } from "@/Calls/callStaffAuth";
import { LucideIcon } from "lucide-react";

interface StatCard {
    label: string;
    value: string;
    icon: LucideIcon;
    color: string;
}

interface QuickLink {
    label: string;
    to: string;
}

interface Props {
    portalName: string;
    roleLabel?: string;
    statCards: StatCard[];
    quickLinks?: QuickLink[];
    children?: React.ReactNode;
}

export default function StaffDashboard({ portalName, roleLabel, statCards, quickLinks, children }: Props) {
    const [staff, setStaff] = useState<StaffUser | null>(null);

    useEffect(() => {
        getCurrentStaff().then((r) => { if (r.ok && r.staff) setStaff(r.staff); });
    }, []);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    {staff ? `Welcome, ${staff.first_name}` : `${portalName} Dashboard`}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    {staff ? `${roleLabel ?? staff.role} · ${portalName} Portal` : portalName}
                </p>
            </div>

            <div className={`grid grid-cols-2 ${statCards.length > 2 ? "lg:grid-cols-4" : "lg:grid-cols-2"} gap-4`}>
                {statCards.map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="bg-card rounded-xl border p-4 flex items-center gap-4">
                        <Icon className={`h-8 w-8 ${color}`} />
                        <div>
                            <p className="text-xs text-muted-foreground">{label}</p>
                            <p className="text-2xl font-bold">{value}</p>
                        </div>
                    </div>
                ))}
            </div>

            {quickLinks && quickLinks.length > 0 && (
                <div className="bg-card rounded-xl border p-6">
                    <h2 className="text-base font-semibold mb-4">Quick Links</h2>
                    <div className="flex flex-wrap gap-3">
                        {quickLinks.map(({ label, to }) => (
                            <a
                                key={to}
                                href={to}
                                className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium hover:bg-muted transition"
                            >
                                {label}
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {children}
        </div>
    );
}
